import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Table, Card, Button, Tag, Space, Modal, Form, Input, Select, DatePicker, message, Badge, Descriptions, Divider, Popconfirm, Typography, Image, Tooltip
} from 'antd';
import { 
  SearchOutlined, FileExcelOutlined, EyeOutlined, CheckCircleOutlined,
  CloseCircleOutlined, DownloadOutlined, FilterOutlined, SyncOutlined, FolderViewOutlined
} from '@ant-design/icons';
import { manualPaymentService, ManualPaymentFilter } from '../../services/manualPaymentService';
import { ManualPayment } from '../../types/transaction.types';
import { getAgencyName } from '../../utils/nameResolver';
import moment from 'moment';
import { Tabs } from 'antd';

const { Option } = Select;
const { TabPane } = Tabs;

interface PaymentDetailsState {
  visible: boolean;
  paymentId: number | null;
  payment: ManualPayment | null;
  loading: boolean;
  processingNotes: string;
  agencyName: string;
}

type TabStatus = 'REQUESTED' | 'PENDING' | 'COMPLETED_AND_CONSUMED' | 'CANCELED';

const ManualPaymentsAdmin: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState<ManualPayment[]>([]);
  const [activeTab, setActiveTab] = useState<TabStatus>('REQUESTED');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [paymentDetails, setPaymentDetails] = useState<PaymentDetailsState>({
    visible: false,
    paymentId: null,
    payment: null,
    loading: false,
    processingNotes: '',
    agencyName: ''
  });
  const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [selectedPaymentId, setSelectedPaymentId] = useState<number | null>(null);
  const [totalByStatus, setTotalByStatus] = useState<Record<string, number>>({});

  const fetchPayments = useCallback(async (status: TabStatus, page: number, size: number) => {
    try {
      setLoading(true);
      console.log("Fetching payments:", { status, page: page - 1, size });
      
      const response = await manualPaymentService.getAllPayments(
        page - 1,
        size,
        status
      );

      setPayments(response.content || []);
      setPagination(prev => ({
        ...prev,
        current: page,
        pageSize: size,
        total: response.totalElements || 0
      }));

      setTotalByStatus(response.totalByStatus || {});
    } catch (error) {
      console.error('Error fetching manual payments:', error);
      message.error(t('admin.manualPayments.loadError'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPayments(activeTab, pagination.current, pagination.pageSize);
  }, [activeTab]);

  const handleTabChange = (activeKey: string) => {
    const newStatus = activeKey as TabStatus;
    setActiveTab(newStatus);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (newPagination: any) => {
    fetchPayments(activeTab, newPagination.current, newPagination.pageSize);
  };

  const handleViewDetails = async (paymentId: number) => {
    setPaymentDetails(prev => ({ ...prev, visible: true, paymentId, loading: true }));

    try {
      const payment = await manualPaymentService.getPaymentDetails(paymentId);
      const agencyName = await getAgencyName(payment.user.id);

      setPaymentDetails({
        visible: true,
        paymentId,
        payment,
        loading: false,
        processingNotes: payment.processingNotes || '',
        agencyName
      });
    } catch (error) {
      console.error('Error fetching payment details:', error);
      message.error(t('admin.manualPayments.detailsLoadError'));
      setPaymentDetails(prev => ({ ...prev, loading: false, agencyName: '' }));
    }
  };

  const handleModalClose = () => {
    setPaymentDetails({
      visible: false,
      paymentId: null,
      payment: null,
      loading: false,
      processingNotes: '',
      agencyName: ''
    });
  };

  const handleApprovePayment = async (paymentId: number) => {
    try {
      await manualPaymentService.approvePayment(paymentId, paymentDetails.processingNotes);
      message.success(t('admin.manualPayments.approveSuccess'));
      handleModalClose();
      fetchPayments(activeTab, pagination.current, pagination.pageSize);
    } catch (error) {
      console.error('Error approving payment:', error);
      message.error(t('admin.manualPayments.approveError'));
    }
  };

  const showRejectModal = (paymentId: number) => {
    setSelectedPaymentId(paymentId);
    setIsRejectModalVisible(true);
  };

  const handleConfirmReject = async () => {
    if (!rejectReason.trim()) {
      message.error(t('admin.manualPayments.rejectReasonRequired'));
      return;
    }

    if (selectedPaymentId == null) return;

    try {
      await manualPaymentService.rejectPayment(selectedPaymentId, rejectReason);
      message.success(t('admin.manualPayments.rejectSuccess'));
      setIsRejectModalVisible(false);
      setRejectReason('');
      handleModalClose();
      fetchPayments(activeTab, pagination.current, pagination.pageSize);
    } catch (error) {
      console.error('Error rejecting payment:', error);
      message.error(t('admin.manualPayments.rejectError'));
    }
  };

  const handleDownloadAttachment = async (paymentId: number) => {
    try {
      const blob = await manualPaymentService.downloadAttachment(paymentId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payment-${paymentId}-attachment.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading attachment:', error);
      message.error(t('admin.manualPayments.attachmentDownloadError'));
    }
  };

  const handleExportReport = async () => {
    try {
      const blob = await manualPaymentService.generateReport({ status: activeTab });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `manual-payments-report-${moment().format('YYYY-MM-DD')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      message.success(t('admin.manualPayments.exportSuccess'));
    } catch (error) {
      console.error('Error exporting report:', error);
      message.error(t('admin.manualPayments.exportError'));
    }
  };

  const renderStatusTag = (status: string) => {
    return <Tag>{t(`paymentStatus.${status}`, status)}</Tag>;
  };

  const renderPaymentMethodTag = (method: string) => {
    const colorMap: Record<string, string> = {
      BANK_TRANSFER: 'blue',
      CHECK: 'purple',
      CASH: 'green',
      CARD: 'red',
      OTHER: 'orange'
    };
    return <Tag color={colorMap[method] || 'default'}>{t(`paymentMethod.${method}`, method)}</Tag>;
  };

  const columns = [
    { title: t('common.reference'), dataIndex: 'externalReference', key: 'externalReference' },
    { title: t('common.agency'), dataIndex: 'agencyName', key: 'agencyName' },
    { title: t('common.amount'), dataIndex: 'amount', key: 'amount', render: (amount: number) => `€${amount?.toFixed(2)}` },
    { title: t('common.method'), dataIndex: 'method', key: 'method', render: renderPaymentMethodTag },
    { title: t('common.date'), dataIndex: 'date', key: 'date', render: (date: string) => moment(date).format('DD/MM/YYYY HH:mm') },
    { title: t('common.status'), dataIndex: 'status', key: 'status', render: renderStatusTag },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_: any, record: ManualPayment) => (
        <Space>
          <Tooltip title={t('admin.manualPayments.tooltipViewDetails')}>
            <Button icon={<EyeOutlined />} size="small" onClick={() => handleViewDetails(record.id)} />
          </Tooltip>
          {record.attachmentUrl && (
            <Tooltip title={t('admin.manualPayments.tooltipDownloadAttachment')}>
              <Button icon={<DownloadOutlined />} size="small" onClick={() => handleDownloadAttachment(record.id)} />
            </Tooltip>
          )}
        </Space>
      )
    }
  ];

  return (
    <Card
      title={t('admin.manualPayments.cardTitle')}
      extra={
        <Space>
          <Button icon={<FileExcelOutlined />} onClick={handleExportReport}>{t('common.export')}</Button>
          <Button icon={<FilterOutlined />}>{t('common.advancedFilters')}</Button>
          <Button icon={<SyncOutlined />} onClick={() => fetchPayments(activeTab, pagination.current, pagination.pageSize)} loading={loading}>{t('common.refresh')}</Button>
        </Space>
      }
    >
      <Tabs activeKey={activeTab} onChange={handleTabChange}>
        {(['REQUESTED', 'PENDING', 'COMPLETED_AND_CONSUMED', 'CANCELED'] as TabStatus[]).map(status => (
          <TabPane
            tab={<Badge count={totalByStatus[status] || 0}>{status}</Badge>}
            key={status}
          >
            <Table
              columns={columns}
              dataSource={payments}
              rowKey="id"
              loading={loading}
              pagination={pagination}
              onChange={handleTableChange}
            />
          </TabPane>
        ))}
      </Tabs>

      {/* Modal Détails */}
      <Modal
        title={t('admin.manualPayments.detailsModalTitle')}
        visible={paymentDetails.visible}
        onCancel={handleModalClose}
        width={800}
        footer={null}
      >
        {paymentDetails.loading ? (
          <div style={{ textAlign: 'center', padding: 20 }}>{t('admin.manualPayments.loading')}</div>
        ) : paymentDetails.payment ? (
          <>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="ID">{paymentDetails.payment.id}</Descriptions.Item>
              <Descriptions.Item label={t('common.reference')}>{paymentDetails.payment.reference}</Descriptions.Item>
              <Descriptions.Item label={t('common.agency')}>{paymentDetails.agencyName || t('common.loading')}</Descriptions.Item>
              <Descriptions.Item label={t('common.status')}>{renderStatusTag(paymentDetails.payment.status)}</Descriptions.Item>
              <Descriptions.Item label={t('common.amount')}>€{paymentDetails.payment.amount?.toFixed(2)}</Descriptions.Item>
              <Descriptions.Item label={t('common.paymentMethod')}>{renderPaymentMethodTag(paymentDetails.payment.method)}</Descriptions.Item>
              <Descriptions.Item label={t('admin.manualPayments.paymentDate')}>{moment(paymentDetails.payment.paymentDate).format('DD/MM/YYYY')}</Descriptions.Item>
              <Descriptions.Item label={t('admin.manualPayments.submissionDate')}>{moment(paymentDetails.payment.submissionDate).format('DD/MM/YYYY HH:mm')}</Descriptions.Item>
            </Descriptions>

            {paymentDetails.payment.description && (
              <>
                <Divider orientation="left">{t('common.description')}</Divider>
                <p>{paymentDetails.payment.description}</p>
              </>
            )}

            {paymentDetails.payment.attachmentUrl && (
              <>
                <Divider orientation="left">{t('admin.manualPayments.attachment')}</Divider>
                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                  {paymentDetails.payment.attachmentUrl.endsWith('.pdf') ? (
                    <Button type="primary" icon={<FolderViewOutlined />} onClick={() => window.open(paymentDetails.payment.attachmentUrl, '_blank')}>{t('admin.manualPayments.viewPdf')}</Button>
                  ) : (
                    <Image src={paymentDetails.payment.attachmentUrl} alt="Justificatif" style={{ maxHeight: 300, maxWidth: '100%' }} />
                  )}
                </div>
              </>
            )}

            {(paymentDetails.payment.status === 'PENDING' || paymentDetails.payment.status === 'REQUESTED') && (
              <>
                {/* <Divider orientation="left">Traitement du paiement</Divider>
                <Form layout="vertical">
                  <Form.Item label="Notes de traitement">
                    <Input.TextArea rows={4} value={paymentDetails.processingNotes} onChange={e => setPaymentDetails(prev => ({ ...prev, processingNotes: e.target.value }))} placeholder="Notes optionnelles" />
                  </Form.Item>
                </Form> */}
                <div style={{ textAlign: 'right', marginTop: 20 }}>
                  <Space>
                    <Button onClick={handleModalClose}>{t('common.close')}</Button>
                    <Button danger icon={<CloseCircleOutlined />} onClick={() => showRejectModal(paymentDetails.paymentId!)}>{t('common.reject')}</Button>
                    <Popconfirm title={t('admin.manualPayments.confirmApproval')} onConfirm={() => handleApprovePayment(paymentDetails.paymentId!)} okText={t('common.yes')} cancelText={t('common.no')}>
                      <Button type="primary" icon={<CheckCircleOutlined />}>{t('common.approve')}</Button>
                    </Popconfirm>
                  </Space>
                </div>
              </>
            )}
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: 20 }}>{t('admin.manualPayments.noData')}</div>
        )}
      </Modal>

      {/* Modal Rejet */}
      <Modal
        title={t('admin.manualPayments.rejectModalTitle')}
        visible={isRejectModalVisible}
        onOk={handleConfirmReject}
        onCancel={() => { setIsRejectModalVisible(false); setRejectReason(''); }}
        okText="Oui"
        cancelText="Non"
      >
        <p>{t('admin.manualPayments.rejectConfirm')}</p>
        <Form layout="vertical">
          <Form.Item label={t('admin.manualPayments.rejectReason')} required>
            <Input placeholder={t('admin.manualPayments.rejectReasonPlaceholder')} value={rejectReason} onChange={e => setRejectReason(e.target.value)} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default ManualPaymentsAdmin;